import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CardContainer } from "@/components/commons/CardContainer";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { TExamCreateForm } from "@/features/learning-content/types/learningContent";
import { EXAM_STATUS_OPTIONS, EXAM_METHOD_OPTIONS } from "@/features/learning-content/constants/examOptions";
import { YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { dayjs } from "@/utils/dayjs";
import { Grid, Stack, Typography } from "@mui/material";

type TBasicInfoFormProps = {
  /** Lock exam method select when the latest record already uses CBT */
  disableExamMethod?: boolean;
};

export const BasicInfoForm: FC<TBasicInfoFormProps> = ({ disableExamMethod = false }) => {
  const { control, setValue, clearErrors, trigger } = useFormContext<TExamCreateForm>();

  const examMethod = useWatch({ control, name: "examMethod" });
  const examMonth = useWatch({ control, name: "examMonth" });
  const isCBT = examMethod === "cbt";
  const isPaperTest = examMethod === "paper_test";

  // Clear examMonth when switching to CBT
  useEffect(() => {
    if (isCBT) {
      setValue("examMonth", null);
      clearErrors("examMonth");
    }
  }, [isCBT, setValue, clearErrors]);

  // Auto-set status to "ended" when examMonth is in the past, and re-validate status
  useEffect(() => {
    if (examMonth && dayjs(examMonth).isValid() && dayjs(examMonth).isBefore(dayjs(), "month")) {
      setValue("status", "ended");
    }
    if (examMonth) trigger("status");
  }, [examMonth, setValue, trigger]);

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          基本情報
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="periodName" label="時期名" required>
              <Input fullWidth placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="status" label="ステータス" required>
              <Select options={EXAM_STATUS_OPTIONS} />
            </FormField>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="examMethod" label="試験方式" required>
              <Select options={EXAM_METHOD_OPTIONS} readonly={disableExamMethod} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="examMonth" label="試験年月" required={isPaperTest}>
              <InputDatePicker disabled={isCBT} views={["year", "month"]} format={YEAR_MONTH_FORMAT} />
            </FormField>
          </Grid>
        </Grid>

        <FormField control={control} name="examOverview" label="試験概要">
          <Textarea placeholder="入力してください" />
        </FormField>

        <FormField control={control} name="examInfoSource" label="試験情報の情報元（実施機関の公式サイトまたは当社提供記事のみ）" required>
          <Input fullWidth placeholder="入力してください" />
        </FormField>

        <FormField control={control} name="internalMemo" label="いろはな社内メモ">
          <Input fullWidth placeholder="入力してください" />
        </FormField>
      </Stack>
    </CardContainer>
  );
};
